// Core
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['editButton', 'saveButton', 'editField', 'displayValue', 'fileWrapper', 'fileInput', 'fileLabel', 'fileName', 'deleteAttachment'];

  // Target declarations
  declare readonly editButtonTargets: HTMLElement[];
  declare readonly saveButtonTargets: HTMLElement[];
  declare readonly editFieldTargets: HTMLElement[];
  declare readonly displayValueTargets: HTMLElement[];
  declare readonly fileWrapperTargets: HTMLElement[];
  declare readonly fileInputTargets: HTMLInputElement[];
  declare readonly fileLabelTargets: HTMLElement[];
  declare readonly fileNameTargets: HTMLElement[];
  declare readonly deleteAttachmentTargets: HTMLElement[];

  connect() {
    this.setupFileInputs();
    this.setupInputFocusEffects();
  }

  setupFileInputs() {
    // Query all file wrappers within this controller's scope
    const fileWrappers = this.element.querySelectorAll('.file-upload-wrapper');
    fileWrappers.forEach(wrapper => {
      const fileInput = wrapper.querySelector('.file-input-hidden') as HTMLInputElement;
      const label = wrapper.querySelector('.file-upload-label');
      const fileName = wrapper.querySelector('.file-name');
      
      if (fileInput && label && fileName) {
        // Remove existing listeners to avoid duplicates
        const newWrapper = wrapper.cloneNode(true) as HTMLElement;
        wrapper.parentNode?.replaceChild(newWrapper, wrapper);
        
        const newFileInput = newWrapper.querySelector('.file-input-hidden') as HTMLInputElement;
        const newLabel = newWrapper.querySelector('.file-upload-label');
        const newFileName = newWrapper.querySelector('.file-name');
        
        // Make wrapper clickable
        newWrapper.addEventListener('click', (e: Event) => {
          if (e.target !== newFileInput) {
            newFileInput.click();
          }
        });
        
        // Update UI when file is selected
        newFileInput.addEventListener('change', () => {
          if (newFileInput.files && newFileInput.files.length > 0) {
            const file = newFileInput.files[0];
            newFileName.textContent = file.name;
            (newFileName as HTMLElement).style.display = 'block';
            newLabel.innerHTML = '<i class="fa fa-check-circle"></i> File selected';
            (newLabel as HTMLElement).style.color = '#3f83c1';
            newWrapper.classList.add('has-file');
          } else {
            (newFileName as HTMLElement).style.display = 'none';
            newLabel.innerHTML = '<i class="fa fa-cloud-upload"></i> Click to upload file here';
            (newLabel as HTMLElement).style.color = '#666';
            newWrapper.classList.remove('has-file');
          }
        });
      }
    });
  }

  setupInputFocusEffects() {
    this.editFieldTargets.forEach(field => {
      const inputs = field.querySelectorAll('input, select, textarea');
      inputs.forEach((input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        input.addEventListener('focus', function() {
          this.style.borderColor = '#3f83c1';
          this.style.boxShadow = '0 0 0 2px rgba(63, 131, 193, 0.1)';
        });
        input.addEventListener('blur', function() {
          this.style.borderColor = '#ddd';
          this.style.boxShadow = 'none';
        });
      });
    });
  }

  editSection(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const sectionId = button.dataset.sectionId;
    const sectionRow = document.querySelector(`.row.card[data-section-id='${sectionId}']`);
    if (!sectionRow) return;

    // Show all editable fields
    sectionRow.querySelectorAll('.edit-field').forEach((el: HTMLElement) => {
      el.style.display = 'block';
    });

    // Hide all display values
    sectionRow.querySelectorAll('.display-value').forEach((el: HTMLElement) => {
      el.style.display = 'none';
    });

    // Toggle buttons - hide edit, show save and cancel
    button.style.display = 'none';
    const saveButton = sectionRow.querySelector(`.save-section[data-section-id='${sectionId}']`) as HTMLElement;
    const cancelButton = sectionRow.querySelector(`.cancel-section[data-section-id='${sectionId}']`) as HTMLElement;
    
    if (saveButton) {
      saveButton.style.display = 'inline-block';
    }
    if (cancelButton) {
      cancelButton.style.display = 'flex';
      cancelButton.style.justifyContent = 'center';
      cancelButton.style.alignItems = 'center';
    }

    // Reapply focus effects and file inputs for newly visible fields
    this.setupInputFocusEffects();
    this.setupFileInputs();
  }

  cancelEdit(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const sectionId = button.dataset.sectionId;
    const sectionRow = document.querySelector(`.row.card[data-section-id='${sectionId}']`);

    if (!sectionRow) return;

    // Hide all editable fields
    sectionRow.querySelectorAll('.edit-field').forEach((el: HTMLElement) => {
      el.style.display = 'none';
    });

    // Show all display values
    sectionRow.querySelectorAll('.display-value').forEach((el: HTMLElement) => {
      el.style.display = 'inline';
    });

    // Toggle buttons - hide save/cancel, show edit
    const saveButton = sectionRow.querySelector(`.save-section[data-section-id='${sectionId}']`) as HTMLElement;
    const cancelButton = sectionRow.querySelector(`.cancel-section[data-section-id='${sectionId}']`) as HTMLElement;
    const editButton = sectionRow.querySelector(`.edit-section[data-section-id='${sectionId}']`) as HTMLElement;
    
    if (saveButton) saveButton.style.display = 'none';
    if (cancelButton) cancelButton.style.display = 'none';
    if (editButton) editButton.style.display = 'inline-block';
  }

  saveSection(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const sectionId = button.dataset.sectionId;
    const sectionRow = document.querySelector(`.row.card[data-section-id='${sectionId}']`) as HTMLElement;

    if (!sectionRow) return;

    const itemId = sectionRow.dataset.itemId;
    const formData = new FormData();

    // Collect all edited inputs/selects
    sectionRow.querySelectorAll('.edit-field input, .edit-field select, .edit-field textarea').forEach((input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
      if ((input as HTMLInputElement).type === 'file') {
        const fileInput = input as HTMLInputElement;
        if (fileInput.files && fileInput.files.length > 0) {
          formData.append(input.name, fileInput.files[0]);
        }
      } else if (input.tagName === 'SELECT' && (input as HTMLSelectElement).multiple) {
        const select = input as HTMLSelectElement;
        const selectedOptions = Array.from(select.selectedOptions);
        selectedOptions.forEach((option) => {
          formData.append(input.name, option.value);
        });
      } else {
        formData.append(input.name, input.value);
      }
    });

    // Send AJAX request to update section
    fetch(`/items/${itemId}/item_digital_passports/update_section/${sectionId}`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.getCsrfToken(),
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(resp => {
      if (resp.ok) {
        window.location.reload();
      } else {
        alert('Error saving attributes');
      }
    })
    .catch(err => {
      console.error(err);
      alert('Error saving attributes');
    });
  }

  deleteAttachment(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (!confirm('Are you sure you want to remove this attachment?')) {
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const itemId = button.dataset.itemId;
    const attributeId = button.dataset.attributeId;
    const container = button.closest('.file-preview-container');

    fetch(`/items/${itemId}/item_digital_passports/destroy_attachment/${attributeId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.getCsrfToken(),
        'Accept': 'application/json'
      }
    })
    .then(resp => {
      if (resp.ok) {
        if (container) {
          const displayValue = container.closest('.display-value');
          if (displayValue) {
            displayValue.innerHTML = 'No Attachments';
          }
        }
      } else {
        alert('Error removing attachment');
      }
    })
    .catch(err => {
      console.error(err);
      alert('Error removing attachment');
    });
  }

  private getCsrfToken(): string {
    const token = document.querySelector("meta[name='csrf-token']") as HTMLMetaElement;
    return token ? token.getAttribute('content') || '' : '';
  }
}
